import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

/**
 * Plugin key for accessing processing highlight state
 */
export const processingHighlightPluginKey = new PluginKey('processingHighlight')

/**
 * Interface for the processing highlight state
 */
interface ProcessingHighlightState {
  decorations: DecorationSet
  range: { from: number; to: number } | null
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    processingHighlight: {
      /**
       * Set the processing highlight at the given range
       */
      setProcessingHighlight: (range: { from: number; to: number }) => ReturnType
      /**
       * Remove the processing highlight
       */
      unsetProcessingHighlight: () => ReturnType
    }
  }
}

/**
 * TipTap extension for managing processing highlight decorations.
 *
 * Unlike marks, decorations:
 * - Work on ALL content types (including code blocks, blockquotes, etc.)
 * - Don't modify document content
 * - Are managed via plugin state, not document schema
 *
 * This is the standard ProseMirror pattern for transient visual state.
 */
export const ProcessingHighlight = Extension.create({
  name: 'processingHighlight',

  addCommands() {
    return {
      setProcessingHighlight:
        (range: { from: number; to: number }) =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            tr.setMeta(processingHighlightPluginKey, { type: 'set', range })
          }
          return true
        },

      unsetProcessingHighlight:
        () =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            tr.setMeta(processingHighlightPluginKey, { type: 'unset' })
          }
          return true
        }
    }
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: processingHighlightPluginKey,

        state: {
          init(): ProcessingHighlightState {
            return {
              decorations: DecorationSet.empty,
              range: null
            }
          },

          apply(tr, state): ProcessingHighlightState {
            const meta = tr.getMeta(processingHighlightPluginKey)

            if (meta?.type === 'set' && meta.range) {
              const { from, to } = meta.range

              // Create an inline decoration for the highlight
              const decoration = Decoration.inline(from, to, {
                class: 'processing-highlight'
              })

              return {
                decorations: DecorationSet.create(tr.doc, [decoration]),
                range: { from, to }
              }
            }

            if (meta?.type === 'unset') {
              return {
                decorations: DecorationSet.empty,
                range: null
              }
            }

            // Map decorations through document changes
            if (tr.docChanged && state.decorations.size > 0) {
              return {
                decorations: state.decorations.map(tr.mapping, tr.doc),
                range: state.range
                  ? {
                      from: tr.mapping.map(state.range.from),
                      to: tr.mapping.map(state.range.to)
                    }
                  : null
              }
            }

            return state
          }
        },

        props: {
          decorations(state) {
            return processingHighlightPluginKey.getState(state)?.decorations ?? DecorationSet.empty
          }
        }
      })
    ]
  }
})

/**
 * Helper to check if there's an active processing highlight
 */
export function hasProcessingHighlight(editor: { state: { plugins: Plugin[] } }): boolean {
  const state = processingHighlightPluginKey.getState(editor.state as never)
  return state?.range !== null
}
